import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Download, Loader2, Mic, Pause, Radio, Volume2 } from 'lucide-react';

import AtelierVocal from './AtelierVocal';
import { assistantsService, type ContenuSonore, type Voix } from '../../services/api/assistants';

interface Props {
  contenu: ContenuSonore;
  conversationId: number;
  messageId: number;
}

const CARACTERES_PAR_SECONDE = 17;

/**
 * Contenu sonore de communication, prêt à être écouté, retouché et livré.
 *
 * Le découpage en répliques sert à confier chaque locuteur à une voix
 * distincte, pas à produire plusieurs fichiers : c'est le spot monté qu'on
 * porte à la radio, et c'est lui que livre le bouton principal.
 */
const RenduAudio: React.FC<Props> = ({ contenu, conversationId, messageId }) => {
  // Textes et voix vivent ici : le montage doit livrer la version que
  // l'utilisateur vient d'entendre, retouches comprises.
  const [textes, setTextes] = useState<string[]>(
    () => contenu.segments.map((s) => s.texte),
  );
  const [voixParLocuteur, setVoixParLocuteur] = useState<Record<string, string>>({});
  const [catalogue, setCatalogue] = useState<Voix[]>([]);
  const [travail, setTravail] = useState(false);
  const [lecture, setLecture] = useState(false);
  const [erreur, setErreur] = useState('');
  const lecteur = useRef<HTMLAudioElement | null>(null);
  const montage = useRef<{ cle: string; url: string } | null>(null);

  useEffect(() => {
    assistantsService
      .listerVoix()
      .then((r) => setCatalogue(r.voix))
      .catch(() => setErreur("La liste des voix n'a pas pu être chargée."));
  }, []);

  useEffect(
    () => () => {
      lecteur.current?.pause();
      if (montage.current) URL.revokeObjectURL(montage.current.url);
    },
    [],
  );

  // La durée suit les retouches, contrairement à celle enregistrée avec la
  // production : c'est celle-ci qui dit si le spot tient encore sa tranche.
  const secondes = useMemo(
    () =>
      Math.round(
        textes.reduce((total, t) => total + t.trim().length, 0) / CARACTERES_PAR_SECONDE,
      ),
    [textes],
  );

  const cible = contenu.duree_cible_secondes ?? 0;
  const tient = cible > 0 && Math.abs(secondes - cible) / cible <= 0.2;
  const retouche = textes.some((t, i) => t !== contenu.segments[i].texte);

  const obtenirMontage = async () => {
    const cle = JSON.stringify([textes, voixParLocuteur]);
    if (montage.current?.cle === cle) return montage.current.url;

    setTravail(true);
    try {
      const blob = await assistantsService.monterSpot(conversationId, messageId, {
        voix: voixParLocuteur,
        textes: Object.fromEntries(textes.map((t, i) => [String(i), t])),
      });
      if (montage.current) URL.revokeObjectURL(montage.current.url);
      montage.current = { cle, url: URL.createObjectURL(blob) };
      return montage.current.url;
    } finally {
      setTravail(false);
    }
  };

  const ecouterTout = async () => {
    if (lecture) {
      lecteur.current?.pause();
      setLecture(false);
      return;
    }
    setErreur('');
    try {
      const url = await obtenirMontage();
      const audio = new Audio(url);
      audio.onended = () => setLecture(false);
      lecteur.current = audio;
      await audio.play();
      setLecture(true);
    } catch (e: any) {
      setErreur(e.message || "Le spot n'a pas pu être monté.");
    }
  };

  const telechargerTout = async () => {
    setErreur('');
    try {
      const url = await obtenirMontage();
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = `${contenu.titre.replace(/[\\/:*?"<>|]/g, '')}.mp3`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
    } catch (e: any) {
      setErreur(e.message || "Le fichier n'a pas pu être produit.");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 text-sm text-eg-gris">
          <Radio className="h-4 w-4" />
          {contenu.support}
        </div>
        <h2 className="mt-1 text-xl font-semibold text-eg-encre">{contenu.titre}</h2>

        <div
          className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm ${
            tient ? 'bg-eg-valide/10 text-eg-valide' : 'bg-eg-alerte/10 text-eg-encre'
          }`}
        >
          <Clock className="h-3.5 w-3.5" />
          {secondes} s pour une cible de {cible} s
          {tient ? ' — dans la tranche' : ' — à ajuster'}
          {retouche && ' (retouché)'}
        </div>
      </div>

      {/* Le livrable : un seul fichier, monté dans l'ordre des répliques. */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-eg-bord
        bg-eg-papier p-4">
        <button
          type="button"
          onClick={ecouterTout}
          disabled={travail}
          className="inline-flex items-center gap-2 rounded-md bg-eg-ombre px-4 py-2 text-sm
            font-medium text-eg-blanc transition-colors hover:bg-eg-encre disabled:opacity-50"
        >
          {travail ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : lecture ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
          {lecture ? 'Pause' : 'Écouter le spot entier'}
        </button>

        <button
          type="button"
          onClick={telechargerTout}
          disabled={travail}
          className="inline-flex items-center gap-2 rounded-md border border-[#DDE2DD] bg-eg-blanc
            px-4 py-2 text-sm font-medium text-eg-brun transition-colors hover:bg-eg-papier
            disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Télécharger le spot (.mp3)
        </button>

        <span className="text-xs text-eg-gris">
          Les {contenu.segments.length} répliques montées en un seul fichier.
        </span>
      </div>

      {erreur && (
        <p className="rounded-md bg-eg-accent/10 px-3 py-2 text-sm text-eg-accent">{erreur}</p>
      )}

      <div className="space-y-4">
        {contenu.segments.map((segment, index) => (
          <div key={index} className="rounded-md border border-eg-bord bg-eg-blanc p-4">
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-eg-encre">
                <Mic className="h-3.5 w-3.5 text-eg-accent" />
                {segment.locuteur}
              </span>
              {segment.intention && (
                <span className="text-xs italic text-eg-gris">— {segment.intention}</span>
              )}
            </div>

            <AtelierVocal
              texte={textes[index]}
              onTexteChange={(t) =>
                setTextes((precedents) => precedents.map((x, i) => (i === index ? t : x)))
              }
              // La voix est attachée au locuteur, pas à la réplique : un même
              // personnage garde son timbre d'un bout à l'autre du spot.
              voix={voixParLocuteur[segment.locuteur] || ''}
              onVoixChange={(v) =>
                setVoixParLocuteur((precedent) => ({ ...precedent, [segment.locuteur]: v }))
              }
              catalogue={catalogue}
              titre={`${contenu.titre} — ${segment.locuteur}`}
              compact
            />
          </div>
        ))}
      </div>

      {contenu.mentions_obligatoires?.length > 0 && (
        <div className="rounded-md bg-eg-papier px-4 py-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-eg-gris">
            Mentions obligatoires
          </p>
          <ul className="space-y-0.5 text-sm text-eg-brun">
            {contenu.mentions_obligatoires.map((mention, index) => (
              <li key={index}>{mention}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RenduAudio;
